import {
  CSP_LICENSE_TENANTS_LOADING,
  CSP_LICENSE_TENANTS_LOADED,
  CSP_LICENSE_TENANTS_FAILED,
  CSP_LICENSE_TENANT_DETAIL_LOADING,
  CSP_LICENSE_TENANT_DETAIL_LOADED,
  CSP_LICENSE_TENANT_DETAIL_FAILED,
  CSP_LICENSE_TENANT_DETAIL_NOT_FOUND,
  CSP_LICENSE_TENANT_DETAIL_CLEAR,
  CSP_LICENSE_TENANT_SYNC_TRIGGERING,
  CSP_LICENSE_TENANT_SYNC_TRIGGERED,
  CSP_LICENSE_TENANT_SYNC_FAILED,
  CSP_LICENSE_SETTINGS_LOADING,
  CSP_LICENSE_SETTINGS_LOADED,
  CSP_LICENSE_SETTINGS_SAVING,
  CSP_LICENSE_SETTINGS_SAVED,
  CSP_LICENSE_SETTINGS_FAILED,
  CSP_LICENSE_CART_STAGE,
  CSP_LICENSE_CART_REMOVE,
  CSP_LICENSE_CART_CLEAR,
  CSP_LICENSE_REQUEST_SUBMITTING,
  CSP_LICENSE_REQUEST_SUBMITTED,
  CSP_LICENSE_REQUEST_REJECTED,
  CSP_LICENSE_REQUEST_FAILED,
  CSP_LICENSE_REQUEST_LIFECYCLE_PENDING,
  CSP_LICENSE_REQUEST_LIFECYCLE_DONE,
  CSP_LICENSE_MY_REQUESTS_LOADING,
  CSP_LICENSE_MY_REQUESTS_LOADED,
  CSP_LICENSE_MY_REQUESTS_FAILED,
  CSP_LICENSE_PENDING_APPROVALS_LOADING,
  CSP_LICENSE_PENDING_APPROVALS_LOADED,
  CSP_LICENSE_PENDING_APPROVALS_FAILED,
  CSP_LICENSE_AUDIT_LOG_LOADING,
  CSP_LICENSE_AUDIT_LOG_LOADED,
  CSP_LICENSE_AUDIT_LOG_FAILED,
  CSP_LICENSE_PRODUCTS_LOADING,
  CSP_LICENSE_PRODUCTS_LOADED,
  CSP_LICENSE_PRODUCTS_FAILED,
  CSP_LICENSE_PRODUCTS_MUTATING,
  CSP_LICENSE_PRODUCTS_MUTATED,
} from './cspLicensesTypes'
// Use the CSP-Licences-scoped api wrapper so the dev-impersonation
// override headers (X-Dev-Override-Upn / -Roles) are stamped onto
// every request to this feature's endpoints. The wrapper transparently
// falls back to the shared `api` helper when no override is active.
import api from '../Sales/CSPLicenses/cspApi'
import { addErrorToast, addSaveToast } from './companyActionCreators'
import { friendlyFailureMessage } from '../Sales/CSPLicenses/cspLicensesFriendlyError'


export const fetchLicenseTenants = () => async (dispatch) => {
  try {
    dispatch({ type: CSP_LICENSE_TENANTS_LOADING })
    const res = await api.get('/csplicenses/tenants')
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    dispatch({ type: CSP_LICENSE_TENANTS_LOADED, payload: data })
  } catch (err) {
    dispatch({ type: CSP_LICENSE_TENANTS_FAILED })
    dispatch(addErrorToast('Failed to load 365 licence tenants'))
  }
}


export const fetchLicenseTenantDetail = (tenantId) => async (dispatch) => {
  try {
    dispatch({ type: CSP_LICENSE_TENANT_DETAIL_LOADING })
    const res = await api.get(`/csplicenses/tenants/${tenantId}`)
    if (res.status === 404) {
      dispatch({ type: CSP_LICENSE_TENANT_DETAIL_NOT_FOUND })
      return
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    dispatch({ type: CSP_LICENSE_TENANT_DETAIL_LOADED, payload: data })
  } catch (err) {
    dispatch({ type: CSP_LICENSE_TENANT_DETAIL_FAILED })
    dispatch(addErrorToast('Failed to load tenant licence detail'))
  }
}

export const clearLicenseTenantDetail = () => ({ type: CSP_LICENSE_TENANT_DETAIL_CLEAR })


export const syncLicenseTenant = (tenantId) => async (dispatch) => {
  // Hangfire-enqueue style: server returns the job ID immediately. We
  // poll the detail endpoint a few seconds later to surface refreshed
  // data, since DD's rate-limit gate adds ~1s before the upsert lands.
  try {
    dispatch({ type: CSP_LICENSE_TENANT_SYNC_TRIGGERING, payload: tenantId })
    const res = await api.post(`/csplicenses/tenants/${tenantId}/sync`)
    if (!res.ok) {
      const message = await res.text()
      throw new Error(message || `HTTP ${res.status}`)
    }
    dispatch({ type: CSP_LICENSE_TENANT_SYNC_TRIGGERED, payload: tenantId })
    dispatch(addSaveToast('Sync queued — refreshing in a moment...'))
    // Give Hangfire + DD's rate limit time to land before re-fetching.
    setTimeout(() => dispatch(fetchLicenseTenantDetail(tenantId)), 3000)
  } catch (err) {
    dispatch({ type: CSP_LICENSE_TENANT_SYNC_FAILED, payload: tenantId })
    dispatch(addErrorToast(err.message || 'Failed to trigger tenant sync'))
  }
}


export const fetchLicenseSettings = () => async (dispatch) => {
  try {
    dispatch({ type: CSP_LICENSE_SETTINGS_LOADING })
    const res = await api.get('/csplicenses/settings')
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    dispatch({ type: CSP_LICENSE_SETTINGS_LOADED, payload: data })
  } catch (err) {
    dispatch({ type: CSP_LICENSE_SETTINGS_FAILED })
    dispatch(addErrorToast('Failed to load 365 licence settings'))
  }
}

export const saveLicenseSettings = (settings) => async (dispatch) => {
  try {
    dispatch({ type: CSP_LICENSE_SETTINGS_SAVING })
    const res = await api.put('/csplicenses/settings', settings)
    if (!res.ok) {
      const message = await res.text()
      throw new Error(message || `HTTP ${res.status}`)
    }
    dispatch({ type: CSP_LICENSE_SETTINGS_SAVED })
    // Re-read from the server rather than storing what we just sent. Several
    // fields on this DTO are server-computed and ignored on PUT -
    // isDickerDataTestApiForced and isDickerDataTestApiEffective - so echoing
    // the request payload into state dropped them, and the "Currently routing
    // to" badge read the resulting undefined as LIVE. It looked like saving had
    // switched the feature to production billing; reopening the modal (a real
    // GET) showed the truth. Fetching also means any future server-computed
    // field is picked up here without touching this action.
    await dispatch(fetchLicenseSettings())
    dispatch(addSaveToast('365 licence settings saved'))
  } catch (err) {
    dispatch({ type: CSP_LICENSE_SETTINGS_FAILED })
    dispatch(addErrorToast(err.message || 'Failed to save settings'))
  }
}


// stage: { tenantId, stockCode, skuDisplayName, cspSubscriptionId,
//          previousQuantity, requestedQuantity }
// One staged line per stockCode at a given tenant. Re-staging the same
// stockCode replaces the prior entry so the cart never holds duplicates.
export const stageCartLine = (line) => ({ type: CSP_LICENSE_CART_STAGE, payload: line })
export const removeCartLine = (stockCode) => ({ type: CSP_LICENSE_CART_REMOVE, payload: stockCode })
export const clearCart = () => ({ type: CSP_LICENSE_CART_CLEAR })


export const submitLicenseRequest = (basket) => async (dispatch) => {
  try {
    dispatch({ type: CSP_LICENSE_REQUEST_SUBMITTING })
    const res = await api.post('/csplicenses/requests', basket)
    if (res.status === 422) {
      const data = await res.json()
      dispatch({ type: CSP_LICENSE_REQUEST_REJECTED, payload: data })
      // 422 == pre-persist reject. Surface the explainer message inline
      // rather than a generic toast, since it usually carries an
      // actionable instruction (e.g. "approval workflow not yet
      // available — try a smaller basket").
      dispatch(addErrorToast(data.message || 'Submission rejected.'))
      return { ok: false, payload: data }
    }
    if (!res.ok) {
      const message = await res.text()
      throw new Error(message || `HTTP ${res.status}`)
    }
    const data = await res.json()
    dispatch({ type: CSP_LICENSE_REQUEST_SUBMITTED, payload: data })
    dispatch(clearCart())
    if (data.status === 'Applied') {
      // DD acked 0000 - request is terminal Applied. Microsoft propagates
      // the licence change asynchronously (typically within 30 min); the
      // server's Message text already carries that guidance.
      dispatch(addSaveToast(data.message || 'Applied. It may take up to 30 minutes for the change to appear in the customer’s tenant.'))
    } else if (data.status === 'Pending') {
      // Over-threshold basket waiting on approval. The "what happens
      // next" copy varies by role and is rendered in My Requests; toast
      // copy stays generic.
      dispatch(addSaveToast('Submitted for approval. Track its status in My Requests.'))
    } else {
      // Persisted but not Applied / Pending means the chokepoint ran
      // and DD did NOT ack 0000 - failure surfaces on the My Requests
      // detail panel with the structured error code.
      dispatch(addErrorToast(data.message || 'Dicker Data did not accept the change. Open My Requests to see why.'))
    }
    return { ok: true, payload: data }
  } catch (err) {
    dispatch({ type: CSP_LICENSE_REQUEST_FAILED })
    dispatch(addErrorToast(err.message || 'Failed to submit request'))
    return { ok: false }
  }
}

export const fetchMyLicenseRequests = () => async (dispatch) => {
  try {
    dispatch({ type: CSP_LICENSE_MY_REQUESTS_LOADING })
    const res = await api.get('/csplicenses/requests/mine')
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    dispatch({ type: CSP_LICENSE_MY_REQUESTS_LOADED, payload: data })
  } catch (err) {
    dispatch({ type: CSP_LICENSE_MY_REQUESTS_FAILED })
    dispatch(addErrorToast('Failed to load your requests'))
  }
}

// Cancel is the only lifecycle action that doesn't go through the
// call_dd_inline chokepoint - the server returns 200 = success or 409 =
// can't transition from current state, with no DTO body to inspect.
// Retry / approve / reject persist a request with status=Applied OR
// status=Failed inside one 200 response, so they need the richer
// "inspect data.status" handling in buildDdAttemptAction below.
export const cancelLicenseRequest = (requestId) => async (dispatch) => {
  try {
    dispatch({ type: CSP_LICENSE_REQUEST_LIFECYCLE_PENDING, payload: requestId })
    const res = await api.post(`/csplicenses/requests/${requestId}/cancel`)
    if (!res.ok) {
      const message = await res.text()
      throw new Error(message || `HTTP ${res.status}`)
    }
    dispatch({ type: CSP_LICENSE_REQUEST_LIFECYCLE_DONE, payload: requestId })
    dispatch(addSaveToast('Request cancelled.'))
    dispatch(fetchMyLicenseRequests())
  } catch (err) {
    dispatch({ type: CSP_LICENSE_REQUEST_LIFECYCLE_DONE, payload: requestId })
    dispatch(addErrorToast(err.message || 'Cancel failed.'))
  }
}


export const fetchPendingApprovals = () => async (dispatch) => {
  try {
    dispatch({ type: CSP_LICENSE_PENDING_APPROVALS_LOADING })
    const res = await api.get('/csplicenses/requests/pending')
    // 403 is the expected response for Sales-only users hitting the
    // peer-approval endpoint. The UI hides the Approvals tab for them,
    // but a stale tab click shouldn't surface as a generic error.
    if (res.status === 403) {
      dispatch({ type: CSP_LICENSE_PENDING_APPROVALS_LOADED, payload: [] })
      return
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    dispatch({ type: CSP_LICENSE_PENDING_APPROVALS_LOADED, payload: data })
  } catch (err) {
    dispatch({ type: CSP_LICENSE_PENDING_APPROVALS_FAILED })
    dispatch(addErrorToast('Failed to load pending approvals'))
  }
}

// Approve / Retry / Reject return a Status field, and that - not the HTTP code - says
// what happened. The chokepoint can persist the request and its attempt and still
// land at Failed, so treating 200 as success would misreport it.
//
// successResolver returns a string (success, toast it) or { failed: true, toast }
// (error toast, caller keeps its UI open).
const buildDdAttemptAction = (path, successResolver, opts = {}) => (requestId, body) => async (dispatch) => {
  const { refreshPending = false, refreshMine = true } = opts
  try {
    dispatch({ type: CSP_LICENSE_REQUEST_LIFECYCLE_PENDING, payload: requestId })
    const url = `/csplicenses/requests/${requestId}/${path}`
    const res = body !== undefined ? await api.post(url, body) : await api.post(url)
    if (res.status === 403 || res.status === 404 || res.status === 409 || res.status === 422) {
      const data = await res.json().catch(() => null)
      const message = (data && data.message) || `Action failed (HTTP ${res.status}).`
      dispatch({ type: CSP_LICENSE_REQUEST_LIFECYCLE_DONE, payload: requestId })
      dispatch(addErrorToast(message))
      return { ok: false, payload: data }
    }
    if (!res.ok) {
      const message = await res.text()
      throw new Error(message || `HTTP ${res.status}`)
    }
    const data = await res.json()
    dispatch({ type: CSP_LICENSE_REQUEST_LIFECYCLE_DONE, payload: requestId })

    const resolution = successResolver(data)
    const operationallyFailed = typeof resolution === 'object' && resolution.failed === true
    if (operationallyFailed) {
      dispatch(addErrorToast(resolution.toast))
    } else {
      dispatch(addSaveToast(typeof resolution === 'string' ? resolution : resolution.toast))
    }

    // Refresh whichever surfaces could have changed. Both endpoints are
    // cheap; the caller view re-renders with the new status.
    if (refreshPending) dispatch(fetchPendingApprovals())
    if (refreshMine) dispatch(fetchMyLicenseRequests())

    // ok=false on operational failure keeps approval modals etc. open
    // so the approver sees the failure on the surface they triggered it
    // from instead of being silently bounced back to the queue.
    return { ok: !operationallyFailed, payload: data }
  } catch (err) {
    dispatch({ type: CSP_LICENSE_REQUEST_LIFECYCLE_DONE, payload: requestId })
    dispatch(addErrorToast(err.message || 'Action failed.'))
    return { ok: false }
  }
}

const approveSuccessToast = 'Approved. It may take up to 30 minutes for the change to appear in the customer’s tenant.'
const retrySuccessToast = 'Applied. It may take up to 30 minutes for the change to appear in the customer’s tenant.'

const ddAttemptResolver = (successToast, failurePrefix) => (data) => {
  if (data && data.status === 'Applied') return successToast
  if (data && data.status === 'Failed') {
    const friendly = friendlyFailureMessage(data.message) || data.message || 'Dicker Data did not accept the change.'
    return { failed: true, toast: `${failurePrefix} ${friendly}` }
  }
  // Defensive: any other status (e.g. unexpected Pending) lands here.
  // Treat as success-style toast — the caller's list refresh will
  // surface the actual state.
  return successToast
}

export const approveLicenseRequest = buildDdAttemptAction(
  'approve',
  ddAttemptResolver(approveSuccessToast, 'Approval ran into a problem:'),
  { refreshPending: true, refreshMine: true })

export const retryLicenseRequest = buildDdAttemptAction(
  'retry',
  ddAttemptResolver(retrySuccessToast, 'Retry ran into a problem:'),
  { refreshPending: false, refreshMine: true })

export const rejectLicenseRequest = (requestId, reason) =>
  // Reject never runs the DD chokepoint - status flips to Rejected with
  // no attempt row. A 200 here is unambiguously a success.
  buildDdAttemptAction(
    'reject',
    () => 'Request rejected.',
    { refreshPending: true, refreshMine: true })(requestId, { reason })


// filters: { statuses[], fromUtc, toUtc, requester, company, page, pageSize }
export const fetchAuditLog = (filters = {}) => async (dispatch) => {
  try {
    dispatch({ type: CSP_LICENSE_AUDIT_LOG_LOADING })
    const params = new URLSearchParams()
    if (filters.statuses && filters.statuses.length > 0) {
      params.append('status', filters.statuses.join(','))
    }
    if (filters.fromUtc) params.append('from', filters.fromUtc)
    if (filters.toUtc) params.append('to', filters.toUtc)
    if (filters.requester) params.append('requester', filters.requester)
    if (filters.company) params.append('company', filters.company)
    params.append('page', filters.page || 1)
    params.append('pageSize', filters.pageSize || 50)

    const res = await api.get(`/csplicenses/requests/all?${params.toString()}`)
    // 403 for ITM/SSE-only callers - the tab is hidden for them, but a
    // stale direct-URL hit shouldn't surface as a generic error.
    if (res.status === 403) {
      dispatch({ type: CSP_LICENSE_AUDIT_LOG_LOADED, payload: { rows: [], totalCount: 0, page: 1, pageSize: 50 } })
      return
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    dispatch({ type: CSP_LICENSE_AUDIT_LOG_LOADED, payload: data })
  } catch (err) {
    dispatch({ type: CSP_LICENSE_AUDIT_LOG_FAILED })
    dispatch(addErrorToast('Failed to load Activity Log'))
  }
}


export const fetchLicenseProducts = (filters = {}) => async (dispatch) => {
  try {
    dispatch({ type: CSP_LICENSE_PRODUCTS_LOADING })
    const params = new URLSearchParams()
    if (filters.search) params.append('search', filters.search)
    if (filters.status) params.append('status', filters.status)
    if (filters.pricing) params.append('pricing', filters.pricing)
    params.append('page', filters.page || 1)
    params.append('pageSize', filters.pageSize || 50)

    const res = await api.get(`/csplicenses/products?${params.toString()}`)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    dispatch({ type: CSP_LICENSE_PRODUCTS_LOADED, payload: data })
  } catch (err) {
    dispatch({ type: CSP_LICENSE_PRODUCTS_FAILED })
    dispatch(addErrorToast('Failed to load product list'))
  }
}

// Bulk exclude/include action - takes an array of StockCodes.
// `exclude` = true to add them to the exclusion list, false to remove.
export const setLicenseProductsExcluded = (stockCodes, exclude) => async (dispatch) => {
  if (!stockCodes || stockCodes.length === 0) return
  try {
    dispatch({ type: CSP_LICENSE_PRODUCTS_MUTATING })
    const url = exclude
      ? '/csplicenses/products/bulk-exclude'
      : '/csplicenses/products/bulk-include'
    const res = await api.post(url, { stockCodes })
    if (!res.ok) {
      const message = await res.text()
      throw new Error(message || `HTTP ${res.status}`)
    }
    dispatch({ type: CSP_LICENSE_PRODUCTS_MUTATED })
    dispatch(addSaveToast(exclude
      ? `${stockCodes.length} product(s) disabled in 365 Licences.`
      : `${stockCodes.length} product(s) enabled in 365 Licences.`))
  } catch (err) {
    dispatch({ type: CSP_LICENSE_PRODUCTS_FAILED })
    dispatch(addErrorToast(err.message || 'Failed to update product list'))
  }
}
